// De `FondoSelection` a los campos `background_*` del payload de generación.
//
// Existía dos veces como closure adentro de `InterfazV2` (`buildDesignModeloBackground` y
// `buildCatalogoBackground`), o sea metida en un componente de 8.000 líneas y por lo tanto
// sin un solo test. Greptile lo marcó al sumar el liso con color: la rama nueva podía
// mandar blanco en vez del color y ningún test se enteraba
// (ADR-2026-09-15-el-fondo-liso-con-color-llega-a-modelo).
//
// Acá es una función PURA, sin React ni imports de componentes, para poder testearla.

import {
  COLORES_FONDO_LISO,
  ILUMINACION_BLANCO_DEFAULT,
  TEMPERATURA_BLANCO_DEFAULT,
  promptDeFondoLiso,
} from './coloresDeFondoLiso';
import type { FondoSelection } from './FondoPicker';

/** `background_id` del fondo blanco en la tabla `fondos`. */
export const ID_FONDO_BLANCO = 5;

/**
 * Qué mandar cuando NO hay selección. Es lo único en que difieren los dos modos:
 * design-modelo arranca en Automático y el Catálogo en blanco.
 */
export type FondoPorDefecto = 'ia' | 'blanco';

export function backgroundDeFondo(
  sel: FondoSelection | null | undefined,
  porDefecto: FondoPorDefecto,
): Record<string, unknown> {
  if (!sel) return porDefecto === 'ia' ? { background_mode: 'ia' } : blanco(null);
  if (sel.mode === 'auto') return { background_mode: 'ia' };
  if (sel.mode === 'blanco') {
    // Liso CON COLOR: beige/rosa/negro no tienen fila en `fondos`, así que viajan como
    // descripción con el prompt fijo del catálogo — el mismo camino que Fotos de
    // producto. Tono y luz NO se mandan: son del flujo 'blanco' del backend y por
    // `descripcion` no llegan a ningún lado.
    const promptLiso = promptDeFondoLiso(sel.colorLiso);
    if (promptLiso) {
      return { background_mode: 'descripcion', background_text: promptLiso };
    }
    return blanco(sel);
  }
  if (sel.mode === 'biblioteca') {
    return { background_mode: 'elegido', background_id: sel.id };
  }
  if (sel.mode === 'guardado') {
    return { background_mode: 'guardado', background_file_name: sel.fileName };
  }
  return { background_mode: 'descripcion', background_text: sel.text };
}

/** El blanco clásico. Tono y luz viajan SIEMPRE con su default aunque el usuario no los
 *  toque: el tono es el objetivo al que el backend normaliza la imagen y que viaje
 *  explícito es lo que hace que lo que muestran los chips sea lo que se genera
 *  (ADR-2026-08-14-fondo-blanco-consistente). */
function blanco(sel: FondoSelection | null): Record<string, unknown> {
  const s = sel?.mode === 'blanco' ? sel : null;
  return {
    background_mode: 'blanco',
    background_id: ID_FONDO_BLANCO,
    fondo_blanco_temperatura: s?.temperatura ?? TEMPERATURA_BLANCO_DEFAULT,
    fondo_blanco_iluminacion: s?.iluminacion ?? ILUMINACION_BLANCO_DEFAULT,
  };
}

/** Los ids de color que ofrece el puente del agente, para los modos que los consumen. */
export const OPCIONES_LISO_CON_COLOR = COLORES_FONDO_LISO.filter((c) => c.prompt).map(
  (c) => ({ id: `liso:${c.id}`, etiqueta: `Fondo liso ${c.id}` }),
);
